/**
 * Фабрика ПАРЫ роутов slug-сущности (ТЗ SEO-8 §5.4).
 *
 * У каждой slug-сущности обязаны быть ДВЕ формы пути:
 *   * `:slug/:key` — канонический URL, на него уводит резолвер;
 *   * `:key`       — без slug. Без него в CSR путь `/news/9` дал бы 404 ЕЩЁ ДО того,
 *                    как сработает нормализация (никакого 301 в CSR нет), а в SSR
 *                    он нужен сущностям с пустым заголовком — slug для них не
 *                    строится вовсе.
 *
 * Это инвариант, а не стилистика: раздел с одной формой ломается наполовину —
 * либо в CSR, либо на безымянных сущностях. Раньше пара выписывалась вручную в
 * пяти файлах (news, promotions, pages, faq, product) вместе с одним и тем же
 * четырёхстрочным комментарием, и ничто не мешало добавить раздел с одной формой
 * или навесить `resolve` только на неё. Фабрика делает такое состояние
 * невыразимым: обе формы и резолвер приезжают одним вызовом.
 *
 * `route-order.spec.ts` продолжает проверять результат по РЕАЛЬНОМУ массиву
 * роутов, то есть контракт закреплён независимо от способа его записи.
 */
import {Route, Routes} from '@angular/router';
import {RESOLVED_ENTITY_KEY} from './resolved-entity';
import {slugCanonicalResolver, SLUG_ROUTES} from './slug-canonical.resolver';
import {EntityKind} from './url-builder';

/**
 * Всё, кроме `path` и `resolve` — их задаёт фабрика. Формы, задающие детей или
 * редирект, исключены: у slug-карточки их не бывает, а `Omit` документирует это
 * на уровне типа.
 */
type SlugRouteBase = Omit<Route, 'path' | 'resolve' | 'children' | 'loadChildren' | 'redirectTo'>;

/**
 * @param kind   раздел — им резолвер выбирает и эндпоинт заголовка, и имя параметра
 *   ключа (`SLUG_ROUTES`, единственный источник и того, и другого)
 * @param base   общая часть обеих форм (`loadComponent`, `canActivate`, `data`)
 * @param prefix ведущий сегмент для форм, объявленных ПЛОСКИМИ соседями, а не
 *   детьми (`'catalog/'`). Каталогу родитель противопоказан: лишний узел в дереве
 *   роутов ломает крошки — разбор в `cabinet.routes.ts`
 *
 * @returns `[':slug/:key', ':key']` — порядок для читаемости, конфликта по числу
 *   сегментов между формами нет
 */
export function slugRoutes(kind: EntityKind, base: SlugRouteBase, prefix = ''): Routes {
  const {param} = SLUG_ROUTES[kind];
  // `{...base}` НЕ копирует `data` вглубь осознанно: и роутер, и форк xng-breadcrumb
  // читают его только на чтение (`breadcrumbs.service.ts` — единственный потребитель).
  const form = (path: string): Route => ({
    ...base,
    path: prefix + path,
    resolve: {[RESOLVED_ENTITY_KEY]: slugCanonicalResolver(kind)},
  });

  return [form(`:slug/:${param}`), form(`:${param}`)];
}
